import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from "@mui/material";
import { CheckCircle, AccessTime, Close } from "@mui/icons-material";
import api from "../../api/axios";
import socket from "../../socket";

const VendorOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const vendor = JSON.parse(localStorage.getItem("user"));

  // ⛔ Protect vendor route
  useEffect(() => {
    if (!vendor?._id) {
      navigate("/vendor/login", { replace: true });
    }
  }, [vendor, navigate]);

  // 📦 Fetch order
  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/vendor/orders/${orderId}`, {
        params: { vendorId: vendor._id },
      });
      setOrder(data);

      /* Join vendor room for real-time updates */
      socket.emit("vendor_join", vendor._id);
    } catch (error) {
      console.error("Failed to load order:", error);
      navigate("/vendor/orders", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [orderId, vendor?._id, navigate]);

  useEffect(() => {
    if (vendor?._id && orderId) {
      fetchOrder();
    }
  }, [fetchOrder]);

  // 📡 Listen for real-time updates
  useEffect(() => {
    socket.on("vendor_order_updated", (data) => {
      if (data.orderId === orderId) {
        setOrder((prev) => ({
          ...prev,
          vendorPhase: data.vendorPhase,
          agentPhase: data.agentPhase,
          deliveryPhase: data.deliveryPhase,
          tracking: data.tracking,
        }));
      }
    });

    socket.on("agent_accepted", (data) => {
      if (data.orderId === orderId) {
        fetchOrder();
      }
    });

    return () => {
      socket.off("vendor_order_updated");
      socket.off("agent_accepted");
    };
  }, [orderId, fetchOrder]);

  // 🟣 PHASE 1: VENDOR ACTIONS
  const handleAccept = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "ACCEPT",
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
    } catch (error) {
      console.error("Failed to accept order:", error);
      alert(error.response?.data?.message || "Failed to accept order");
    } finally {
      setLoading(false);
    }
  };

  const handleStartPreparing = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "PREPARING",
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
    } catch (error) {
      console.error("Failed to start preparing order:", error);
      alert(error.response?.data?.message || "Failed to start preparing order");
    } finally {
      setLoading(false);
    }
  };
  const handleReject = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "REJECT",
        reason: rejectionReason,
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
      setRejectDialog(false);
      setRejectionReason("");
    } catch (error) {
      console.error("Failed to reject order:", error);
      alert(error.response?.data?.message || "Failed to reject order");
    } finally {
      setLoading(false);
    }
  };

  const handleReadyForPickup = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "READY_FOR_PICKUP",
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
    } catch (error) {
      console.error("Failed to mark ready:", error);
      alert(error.response?.data?.message || "Failed to mark items as ready");
    } finally {
      setLoading(false);
    }
  };

  if (!order) return null;

  const vendorPhase = order.vendorPhase || {};
  const agentPhase = order.agentPhase || {};
  const deliveryPhase = order.deliveryPhase || {};

  return (
    <Box sx={{ p: 4 }}>
      {/* Header */}
      <Button onClick={() => navigate("/vendor/orders")} sx={{ mb: 2 }}>
        ← Back to Orders
      </Button>

      <Typography variant="h5" fontWeight={700} sx={{ mb: 4 }}>
        Order #{orderId.substring(0, 8).toUpperCase()}
      </Typography>

      <Grid container spacing={3}>
        {/* Left: Order Items */}
        <Grid item xs={12} md={8}>
          {/* Items */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Order Items
              </Typography>
              {order.items?.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 1.5,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Qty: {item.qty}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{item.price}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e0e0e0" }}>
                <Typography variant="body2" fontWeight={700}>
                  Total: ₹{order.total}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Payment: {order.paymentMethod?.toUpperCase()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Delivery Address */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Delivery Address
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {order.address?.addressLine1}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {order.address?.city}, {order.address?.state}{" "}
                {order.address?.pincode}
              </Typography>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Order Timeline
              </Typography>
              <Stack spacing={1}>
                {order.tracking?.map((track, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: 1.5,
                      backgroundColor: track.completed ? "#f0f7ff" : "#fafafa",
                      borderRadius: 1,
                      borderLeft: track.completed
                        ? "4px solid #4caf50"
                        : "4px solid #ccc",
                    }}
                  >
                    <CheckCircle
                      sx={{
                        fontSize: 20,
                        color: track.completed ? "#4caf50" : "#ccc",
                        mr: 2,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {track.label}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(track.time).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Vendor Phase Controls */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 20 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                🟣 Your Responsibility
              </Typography>

              {/* Vendor Phase Status */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="textSecondary">
                  Preparation Status
                </Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Chip
                    icon={<CheckCircle />}
                    label={vendorPhase.status || "PENDING"}
                    color={
                      vendorPhase.status === "ACCEPTED"
                        ? "success"
                        : vendorPhase.status === "REJECTED"
                        ? "error"
                        : "default"
                    }
                    sx={{ width: "100%" }}
                  />
                </Box>
              </Box>

              {/* PHASE 1: PENDING - Show Accept/Reject */}
              {vendorPhase.status === "PENDING" && (
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handleAccept}
                    disabled={loading}
                  >
                    ✓ Accept Order
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={() => setRejectDialog(true)}
                    disabled={loading}
                    startIcon={<Close />}
                  >
                    ✗ Reject Order
                  </Button>
                </Stack>
              )}

              {/* PHASE 1: ACCEPTED - Show Start Preparing */}
              {vendorPhase.status === "ACCEPTED" && (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ✓ Order Accepted
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Click below to start preparing the order
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, backgroundColor: "#f0f7ff", borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Estimated Ready Time
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
                      <AccessTime sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }} />
                      {vendorPhase.estimatedReadyTime
                        ? new Date(vendorPhase.estimatedReadyTime).toLocaleTimeString()
                        : "30 mins"}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleStartPreparing}
                    disabled={loading}
                  >
                    ▶ Start Preparing
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={() => setRejectDialog(true)}
                    disabled={loading}
                    startIcon={<Close />}
                  >
                    ✗ Reject Order
                  </Button>
                </Stack>
              )}

              {/* PHASE 1: PREPARING - Can upload images & mark ready */}
              {vendorPhase.status === "PREPARING" && (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, backgroundColor: "#fff3e0", borderRadius: 1, border: "1px solid #ff9800" }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ⏳ Preparing Order
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      You can upload preparation images and notes. Click "Ready for Pickup" when done.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleReadyForPickup}
                    disabled={loading}
                  >
                    ✓ Ready for Pickup
                  </Button>
                </Stack>
              )}

              {/* PHASE 1: READY_FOR_PICKUP - Waiting for Agent */}
              {vendorPhase.status === "READY_FOR_PICKUP" && (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ✓ Items Ready for Pickup
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Waiting for agent to arrive...
                    </Typography>
                  </Box>

                  {/* Show Agent Status */}
                  {agentPhase.status === "SEARCHING" && (
                    <Box sx={{ p: 2, backgroundColor: "#fff3e0", borderRadius: 1, border: "1px solid #ff9800" }}>
                      <Typography variant="caption" color="textSecondary">
                        🔍 Agent Status
                      </Typography>
                      <Chip
                        label="Agents Searching"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  )}

                  {agentPhase.status === "ACCEPTED" && (
                    <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        ✓ Agent Assigned
                      </Typography>
                      <Chip
                        label="Agent Coming to Pickup"
                        size="small"
                        color="success"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  )}

                  {agentPhase.status === "NONE" && (
                    <Box sx={{ p: 2, backgroundColor: "#ffebee", borderRadius: 1 }}>
                      <Typography variant="caption" color="error">
                        ⚠️ No agents available
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}

              {/* PHASE 1: REJECTED */}
              {vendorPhase.status === "REJECTED" && (
                <Box sx={{ p: 2, backgroundColor: "#ffebee", borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    ✗ Order Rejected
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {vendorPhase.rejectionReason}
                  </Typography>
                </Box>
              )}

              {/* Divider */}
              <Box sx={{ my: 3, borderTop: "1px solid #e0e0e0" }} />

              {/* Show Agent & Delivery Status */}
              {agentPhase.status !== "NONE" && (
                <>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: "block" }}>
                    🟠 Agent Phase
                  </Typography>
                  <Chip
                    label={agentPhase.status}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {deliveryPhase.status !== "NONE" && (
                <>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: "block" }}>
                    🟡 Delivery Phase
                  </Typography>
                  <Chip
                    label={deliveryPhase.status}
                    size="small"
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
        <DialogTitle>Reject Order</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for rejection"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a reason for rejecting this order"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={loading || !rejectionReason.trim()}
          >
            {loading ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorOrderDetails;


